import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { TelehealthPrerequisitesFormValues } from "./interface";
import useGetTelehealthPrerequisites from "hooks/api/client/useGetTelehealthPrerequisites";
import useUpsertTelehealthPrerequisites from "hooks/api/client/useUpsertTelehealthPrerequisites";

const EMPTY_VALUES: TelehealthPrerequisitesFormValues = {
  looksAtScreenOnName: "",
  comesToWorkArea: "",
  respondsToMasteredInstructions: "",
  identifiesItemsOnScreen: "",
  respondsToPraise: "",
  remainedSeatedWithoutSupportMinutes: "",
  remainedSeatedWithoutSupportTotal: "",
  remainedSeatedWithoutSupportNA: null,
  remainedSeatedWithSupportMinutes: "",
  remainedSeatedWithSupportTotal: "",
  remainedSeatedWithSupportNA: null,
  attendedNoSupportMinutes: "",
  attendedNoSupportTotal: "",
  attendedNoSupportNA: null,
  attendedWithSupportMinutes: "",
  attendedWithSupportTotal: "",
  attendedWithSupportNA: null,
  individualizedFactors: "",
  participationStrategies: "",
};

export const PrerequisitesForTelehealth: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { telehealthPrerequisites, refetchTelehealthPrerequisites, isFetched: isTelehealthPrerequisitesFetched } =
    useGetTelehealthPrerequisites(clientIdNum);

  const { upsertTelehealthPrerequisitesMutation, isUpsertTelehealthPrerequisitesLoading } =
    useUpsertTelehealthPrerequisites({
      onSuccess: () => {
        refetchTelehealthPrerequisites();
        setEditMode(false);
      },
    });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<TelehealthPrerequisitesFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] =
    useState<TelehealthPrerequisitesFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (telehealthPrerequisites) {
      const tp = telehealthPrerequisites as Record<string, any>;
      const values: TelehealthPrerequisitesFormValues = {
        looksAtScreenOnName: tp.looks_at_screen_on_name ?? "",
        comesToWorkArea: tp.comes_to_work_area_on_request ?? "",
        respondsToMasteredInstructions: tp.responds_to_instructions ?? "",
        identifiesItemsOnScreen: tp.identifies_items_on_screen ?? "",
        respondsToPraise: tp.responds_to_social_praise ?? "",
        remainedSeatedWithoutSupportMinutes: tp.remained_seated_without_support?.toString() ?? "",
        remainedSeatedWithoutSupportTotal: tp.seated_without_support_total?.toString() ?? "",
        remainedSeatedWithoutSupportNA: tp.seated_without_support_not_applicable ?? null,
        remainedSeatedWithSupportMinutes: tp.remained_seated_with_support?.toString() ?? "",
        remainedSeatedWithSupportTotal: tp.seated_with_support_total?.toString() ?? "",
        remainedSeatedWithSupportNA: tp.seated_with_support_not_applicable ?? null,
        attendedNoSupportMinutes: tp.attended_screen_without_support?.toString() ?? "",
        attendedNoSupportTotal: tp.attended_without_support_total?.toString() ?? "",
        attendedNoSupportNA: tp.attended_without_support_not_applicable ?? null,
        attendedWithSupportMinutes: tp.attended_screen_with_support?.toString() ?? "",
        attendedWithSupportTotal: tp.attended_with_support_total?.toString() ?? "",
        attendedWithSupportNA: tp.attended_with_support_not_applicable ?? null,
        individualizedFactors: tp.individualized_factors ?? "",
        participationStrategies: tp.participation_strategies ?? "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [telehealthPrerequisites]);

  const handleChange =
    (field: keyof TelehealthPrerequisitesFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleToggleNA =
    (field: keyof TelehealthPrerequisitesFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setFormValues((prev) => ({ ...prev, [field]: checked }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertTelehealthPrerequisitesMutation({
      clientId: clientIdNum,
      payload: {
        looks_at_screen_on_name: formValues.looksAtScreenOnName || null,
        comes_to_work_area_on_request: formValues.comesToWorkArea || null,
        responds_to_instructions: formValues.respondsToMasteredInstructions || null,
        identifies_items_on_screen: formValues.identifiesItemsOnScreen || null,
        responds_to_social_praise: formValues.respondsToPraise || null,
        remained_seated_without_support:
          !formValues.remainedSeatedWithoutSupportNA && formValues.remainedSeatedWithoutSupportMinutes
            ? Number(formValues.remainedSeatedWithoutSupportMinutes)
            : null,
        seated_without_support_total:
          !formValues.remainedSeatedWithoutSupportNA && formValues.remainedSeatedWithoutSupportTotal
            ? Number(formValues.remainedSeatedWithoutSupportTotal)
            : null,
        seated_without_support_not_applicable: formValues.remainedSeatedWithoutSupportNA,
        remained_seated_with_support:
          !formValues.remainedSeatedWithSupportNA && formValues.remainedSeatedWithSupportMinutes
            ? Number(formValues.remainedSeatedWithSupportMinutes)
            : null,
        seated_with_support_total:
          !formValues.remainedSeatedWithSupportNA && formValues.remainedSeatedWithSupportTotal
            ? Number(formValues.remainedSeatedWithSupportTotal)
            : null,
        seated_with_support_not_applicable: formValues.remainedSeatedWithSupportNA,
        attended_screen_without_support:
          !formValues.attendedNoSupportNA && formValues.attendedNoSupportMinutes
            ? Number(formValues.attendedNoSupportMinutes)
            : null,
        attended_without_support_total:
          !formValues.attendedNoSupportNA && formValues.attendedNoSupportTotal
            ? Number(formValues.attendedNoSupportTotal)
            : null,
        attended_without_support_not_applicable: formValues.attendedNoSupportNA,
        attended_screen_with_support:
          !formValues.attendedWithSupportNA && formValues.attendedWithSupportMinutes
            ? Number(formValues.attendedWithSupportMinutes)
            : null,
        attended_with_support_total:
          !formValues.attendedWithSupportNA && formValues.attendedWithSupportTotal
            ? Number(formValues.attendedWithSupportTotal)
            : null,
        attended_with_support_not_applicable: formValues.attendedWithSupportNA,
        individualized_factors: formValues.individualizedFactors || null,
        participation_strategies: formValues.participationStrategies || null,
      },
      isNew: isTelehealthPrerequisitesFetched && !telehealthPrerequisites,
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertTelehealthPrerequisitesLoading;

  if (!isTelehealthPrerequisitesFetched) {
    return (
      <SectionWrapper
        title="Prerequisites for Telehealth"
        description="Assess the client's readiness and baseline behaviors required for effective telehealth participation."
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Prerequisites for Telehealth"
      description="Assess the client's readiness and baseline behaviors required for effective telehealth participation."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields
          formValues={formValues}
          handleChange={handleChange}
          handleToggleNA={handleToggleNA}
        />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default PrerequisitesForTelehealth;
